import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import {
  Panier,
  TypePiece,
  DetailPanier,
  PaymentMethod,
  PieceToAdd,
  PanierState,
  PanierResponse
} from '../models';
import { ReferenceService } from './reference.service';
import * as config from '../config/config';
import { HttpServices } from './http.service';
import { LoaderService } from './loader.service';

@Injectable()
export class PanierService {
  private cartContent: Panier;
  private cartAmount: number;
  private cartState: PanierState;
  private paymentMethod: PaymentMethod;
  private cartContentChange = new Subject<Panier>();
  private paymentSucceeded = new Subject<any>();
  private initialCartAmount: number;
  cartContentChange$ = this.cartContentChange.asObservable();
  paymentSucceeded$ = this.paymentSucceeded.asObservable();

  constructor(
    private referenceService: ReferenceService,
    private httpServices: HttpServices,
    private loaderService: LoaderService
  ) { }

  fetchCart(supplierId: number): Observable<PanierResponse> {
    const params = [
      { 'Key': 'idFournisseur', 'Value': String(supplierId) },
      { 'Key': 'orderBy', 'Value': this.referenceService.staticReferences.DefaultSort[config.SortKey.PANIER_DEFAUT_SORT] }
    ];
    return this.httpServices.get<any>(this.referenceService.staticReferences.Action[config.ApiKeys.GET_PANIER],
      () => { }, params);
  }

  initCart(cartContent: Panier) {
    this.setCartContent(cartContent);
    this.setCartAmount(cartContent.TotalPanier.Total);
    this.setInitialCartAmount(cartContent.TotalPanier.Total);
  }

  getCartState(): PanierState {
    return this.cartState;
  }

  setCartState(state: PanierState): void {
    this.cartState = state;
    if (state === PanierState.PAY_SUCCESS) {
      this.paymentSucceeded.next();
    }
  }

  getCartContent(): Panier {
    return this.cartContent;
  }

  setCartContent(value: Panier): void {
    this.cartContent = value;
  }

  handlePartialPayment(amount: number): void {
    this.setCartAmount(amount);
    this.cartContent.DetailPanier[0].Montant = amount;
  }

  removePieceFromCart(supplierId: number, pieceId: number): void {
    this.loaderService.increaseLoaderCount();
    const pieceToRemove = this.cartContent.DetailPanier.find(pieceInCart => pieceInCart.IdPieceComptable === pieceId);

    if (pieceToRemove) {
      const param = [
        { 'Key': 'orderBy', 'Value': this.referenceService.staticReferences.DefaultSort[config.SortKey.PANIER_DEFAUT_SORT] },
        { 'Key': 'idFournisseur', 'Value': String(supplierId) },
        { 'Key': 'panierId', 'Value': pieceToRemove.Id }
      ];

      this.httpServices.delete<PanierResponse>(config.ApiKeys.DELETE_LIGNE_PANIER, () => { }, param)
        .subscribe(response => {
          this.setCartContent(Panier.extractPanier(response));
          this.setCartAmount(this.cartContent.TotalPanier.Total);
          this.setInitialCartAmount(this.cartContent.TotalPanier.Total);
          this.cartContentChange.next(this.cartContent);
          this.loaderService.decreaseLoaderCount();
        });
    }
  }

  addPiecesToCart(supplierId: number, pieces: PieceToAdd[]): void {
    this.loaderService.increaseLoaderCount();
    const param = [
      { 'Key': 'orderBy', 'Value': this.referenceService.staticReferences.DefaultSort[config.SortKey.PANIER_DEFAUT_SORT] },
      { 'Key': 'idFournisseur', 'Value': String(supplierId) }
    ];

    this.httpServices.put<PieceToAdd[], any>(config.ApiKeys.PUT_AJOUT_PANIER, pieces, () => { }, param).
      subscribe(response => {
        this.setCartContent(Panier.extractPanier(response.panier));
        this.setCartAmount(this.cartContent.TotalPanier.Total);
        this.setInitialCartAmount(this.cartContent.TotalPanier.Total);
        this.cartContentChange.next(this.cartContent);
        this.loaderService.decreaseLoaderCount();
      });
  }

  clearCart(supplierId: number): void {
    this.loaderService.increaseLoaderCount();

    this.httpServices.put<any, any>(config.ApiKeys.PUT_CLEAR_PAYEMENT, '', () => { },
      [{ 'Key': 'idFournisseur', 'Value': String(supplierId) }])
      .subscribe(() => {
        this.initCart(new Panier([], {
          SousTotal: 0,
          Total: 0,
          Tva: 0
        }));
        this.cartContentChange.next(this.cartContent);
        this.loaderService.decreaseLoaderCount();
      });
  }

  getCartAmount(): number {
    return this.cartAmount;
  }

  setCartAmount(amount: number): void {
    this.cartAmount = amount;
  }

  isPaymentPartial(): boolean {
    return this.cartAmount !== this.cartContent.TotalPanier.Total;
  }

  getPiecesByType(type: TypePiece): DetailPanier[] {
    return this.cartContent.DetailPanier.filter(piece => piece.TypePiece === type);
  }

  getPaymentMethod(): PaymentMethod {
    return this.paymentMethod;
  }

  setPaymentMethod(method: PaymentMethod) {
    this.paymentMethod = method;
  }

  getInitialCartAmount(): number {
    return this.initialCartAmount;
  }

  setInitialCartAmount(amount: number) {
    this.initialCartAmount = amount;
  }
}
